import React, { useState, useEffect, useCallback } from 'react';
import { supportApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowsClockwise, Trash, EnvelopeSimple, User, Buildings, Star, Sparkle,
  ChatsCircle, Lightbulb, CheckCircle,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../lib/utils';

const STATUS_OPTIONS = ['new', 'in_progress', 'resolved', 'closed'];
const STATUS_STYLES = {
  new:         { bg: 'bg-[#41B6E6]/15', text: 'text-[#1E7CA6]' },
  in_progress: { bg: 'bg-[#FFD100]/25', text: 'text-[#B5940A]' },
  resolved:    { bg: 'bg-[#00B140]/15', text: 'text-[#00813A]' },
  closed:      { bg: 'bg-[#6B7280]/15', text: 'text-[#4B5563]' },
};

export default function SupportAdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('enquiries'); // enquiries | csat | feature
  const isAdmin = user && user.role === 'admin';

  if (!isAdmin) {
    return (
      <div className="p-10" data-testid="support-admin-only">
        <div className="bg-card border border-border rounded-sm p-8 text-center">
          <p className="font-heading text-lg font-semibold">Admin access required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="support-admin-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Admin · Support</p>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Support &amp; Feedback</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Inbound enquiries from <code className="bg-muted/40 px-1 rounded">/support</code> and survey responses.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-6 overflow-x-auto" data-testid="support-tabs">
          <TabButton active={tab === 'enquiries'} onClick={() => setTab('enquiries')} icon={ChatsCircle} testId="tab-enquiries">
            Enquiries
          </TabButton>
          <TabButton active={tab === 'csat'} onClick={() => setTab('csat')} icon={Star} testId="tab-csat">
            CSAT ratings
          </TabButton>
          <TabButton active={tab === 'feature'} onClick={() => setTab('feature')} icon={Lightbulb} testId="tab-feature">
            Feature requests
          </TabButton>
        </div>
      </div>

      {tab === 'enquiries' ? <EnquiriesPanel /> : <SurveysPanel type={tab} />}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, testId, children }) {
  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-2 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
      }`}
      data-testid={testId}
    >
      <Icon size={16} weight={active ? 'fill' : 'duotone'} />
      {children}
      {active && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#002677]" />}
    </button>
  );
}

// =======================================================================
function EnquiriesPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supportApi.listEnquiries(filter === 'all' ? null : filter);
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load enquiries');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try { await supportApi.updateEnquiry(id, { status }); toast.success(`→ ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return; // eslint-disable-line no-alert
    try { await supportApi.deleteEnquiry(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const stats = STATUS_OPTIONS.reduce((a, s) => { a[s] = items.filter((i) => i.status === s).length; return a; }, {});

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
          {STATUS_OPTIONS.map((s) => {
            const style = STATUS_STYLES[s];
            const active = filter === s;
            return (
              <button
                key={s}
                onClick={() => setFilter(active ? 'all' : s)}
                className={`bg-card border rounded-sm p-3 text-left transition-all ${active ? 'border-[#002677] shadow-md' : 'border-border hover:border-foreground/30'}`}
                data-testid={`filter-${s}`}
              >
                <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{s.replace('_', ' ')}</p>
                <p className="font-heading text-2xl font-semibold mt-1">{stats[s] || 0}</p>
                <span className={`inline-block mt-1 h-1 w-8 rounded-full ${style.bg}`} />
              </button>
            );
          })}
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm ml-3" data-testid="enq-refresh">
          <ArrowsClockwise size={16} />
        </Button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-medium">No enquiries</p>
            <p className="text-sm text-muted-foreground mt-1">New submissions from /support land here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="enquiries-table">
              <thead className="bg-muted/30 border-b border-border">
                <tr>
                  <th className="text-left font-semibold px-5 py-3">From</th>
                  <th className="text-left font-semibold px-5 py-3">Category</th>
                  <th className="text-left font-semibold px-5 py-3">Subject</th>
                  <th className="text-left font-semibold px-5 py-3">Submitted</th>
                  <th className="text-left font-semibold px-5 py-3">Status</th>
                  <th className="text-right font-semibold px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const style = STATUS_STYLES[r.status] || STATUS_STYLES.new;
                  return (
                    <tr key={r.id} className="border-b border-border/50 last:border-b-0 hover:bg-accent/20" data-testid={`enq-row-${r.id}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2"><User size={14} className="text-muted-foreground" /><span className="font-medium">{r.full_name}</span></div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <a href={`mailto:${r.email}?subject=Re:%20${encodeURIComponent(r.subject)}`} className="flex items-center gap-1 hover:text-foreground">
                            <EnvelopeSimple size={12} /> {r.email}
                          </a>
                          {r.company && <span className="flex items-center gap-1"><Buildings size={12} /> {r.company}</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3"><Badge variant="outline" className="capitalize">{r.category || 'other'}</Badge></td>
                      <td className="px-5 py-3 max-w-md">
                        <p className="font-medium">{r.subject}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{r.message}</p>
                      </td>
                      <td className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(r.submitted_at)}</td>
                      <td className="px-5 py-3">
                        <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger className={`rounded-sm h-8 text-xs w-[140px] ${style.bg} border-0 ${style.text} font-medium`} data-testid={`enq-status-${r.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="text-destructive hover:bg-destructive/10" data-testid={`enq-del-${r.id}`}>
                          <Trash size={14} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// =======================================================================
function SurveysPanel({ type }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supportApi.listSurveys(type);
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load surveys'); }
    finally { setLoading(false); }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const avg = type === 'csat' && items.length > 0
    ? (items.reduce((s, i) => s + (i.score || 0), 0) / items.length).toFixed(2)
    : null;

  return (
    <div className="space-y-5">
      {avg && (
        <div className="bg-card border border-border rounded-sm p-5 flex items-center justify-between" data-testid="csat-summary">
          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Average CSAT</p>
            <p className="font-heading text-3xl font-bold mt-1">{avg} <span className="text-base text-muted-foreground font-normal">/ 5.00</span></p>
          </div>
          <p className="text-sm text-muted-foreground">{items.length} response{items.length === 1 ? '' : 's'}</p>
        </div>
      )}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-medium">No {type === 'csat' ? 'ratings' : 'feature requests'} yet</p>
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid={`surveys-${type}`}>
            {items.map((s) => (
              <li key={s.id} className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {type === 'csat' && (
                      <div className="flex items-center gap-1 mb-2" data-testid={`row-${s.id}-stars`}>
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} size={16} weight={n <= (s.score || 0) ? 'fill' : 'regular'} className={n <= (s.score || 0) ? 'text-[#FFD100]' : 'text-muted-foreground/30'} />
                        ))}
                      </div>
                    )}
                    {s.comment && <p className="text-sm leading-relaxed">{s.comment}</p>}
                    {!s.comment && type === 'csat' && <p className="text-sm text-muted-foreground italic">No comment.</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(s.submitted_at)}</p>
                    {s.email && (
                      <a href={`mailto:${s.email}`} className="text-xs text-[#002677] mt-1 hover:underline whitespace-nowrap">{s.email}</a>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
